import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { TokenService } from '../../features/auth/services/token.service';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { catchError, finalize, map, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { SnackbarService } from '../services/snackbar.service';
import { AuthService } from '../../features/auth/services/auth.service';

// Rafraîchissement partagé : plusieurs requêtes en 401 simultanées ne déclenchent qu'un seul refresh.
let refreshInProgress$: Observable<string> | null = null;

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const snackBarService = inject(SnackbarService);
  if (isNoNeedToken(req.url)) {
    return next(req).pipe(
      catchError((err: HttpErrorResponse) => {
        const error = err.error?.message || err.statusText;
        snackBarService.show(error);
        return throwError(() => error);
      })
    );
  } else {
    return next(withToken(req, tokenService.getAccessToken())).pipe(
      catchError((err: HttpErrorResponse) => {
        // Jeton expiré : on tente le refresh puis on rejoue la requête d'origine.
        if (err.status === 401 && tokenService.getRefreshToken()) {
          return refreshAccessToken(authService).pipe(
            switchMap((newToken) => next(withToken(req, newToken))),
            catchError((retryErr) => {
              // Le refresh a réussi : un nouvel échec est une erreur métier
              // (ex : 401 "Vous n'êtes pas l'artisan"), pas une fin de session.
              // Seul un échec du refresh lui-même (erreur déjà transformée en
              // message par la branche sans jeton) déconnecte.
              if (retryErr instanceof HttpErrorResponse) {
                return showError(retryErr, snackBarService);
              }
              return logoutAndNotify(retryErr, tokenService, router, snackBarService);
            })
          );
        }
        if (err.status === 401) {
          // 401 sans refresh token : session morte.
          return logoutAndNotify(err, tokenService, router, snackBarService);
        }
        // 403 et autres statuts : erreur métier (droits, validation…), on affiche
        // sans déconnecter — la couche JWT ne renvoie que des 401.
        return showError(err, snackBarService);
      })
    );
  }
};

function withToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  if (!token) {
    return req;
  }
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

function refreshAccessToken(authService: AuthService): Observable<string> {
  if (!refreshInProgress$) {
    refreshInProgress$ = authService.refreshToken().pipe(
      map((response) => response.token),
      finalize(() => refreshInProgress$ = null),
      shareReplay(1)
    );
  }
  return refreshInProgress$;
}

function showError(err: HttpErrorResponse, snackBarService: SnackbarService) {
  const error = err.error?.message || err.statusText;
  snackBarService.show(error);
  return throwError(() => error);
}

function logoutAndNotify(
  err: HttpErrorResponse | string,
  tokenService: TokenService,
  router: Router,
  snackBarService: SnackbarService
) {
  tokenService.removeToken();
  router.navigateByUrl('/');
  const error = err instanceof HttpErrorResponse ? (err.error?.message || err.statusText) : err;
  snackBarService.show(error);
  return throwError(() => error);
}

function isNoNeedToken (url: string) {
  return url === `${environment.apiURL}/api/login_check`
    || url === `${environment.apiURL}/api/token/refresh`
    || url.includes('https://api-adresse.data.gouv.fr')
    || url.includes('https://homologation-webpayment.payline.com/webpayment/getToken')
    || url.includes('https://webpayment.payline.com/webpayment/getToken');
}
